import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  MenuItem,
  IconButton,
  Chip,
  useTheme,
  TextField,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Skeleton,
  Pagination,
  Divider,
  Tooltip,
  Modal,
  Snackbar,
  SnackbarContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Delete,
  // Add,
  CalendarToday,
  LocationOn,
  Link as LinkIcon,
  EventSeat,
  Lock,
  Public,
  WorkspacePremium,
  HowToReg,
  ToggleOn,
  ToggleOff,
  ShareOutlined,
  Close as CloseIcon,
  Link as CopyLinkIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  WhatsApp as WhatsAppIcon,
  Check as CheckIcon,
  Visibility,
  Search,
  Flag,
  MoreVert,
} from '@mui/icons-material';
import { FacebookShareButton, TwitterShareButton, LinkedinShareButton, WhatsappShareButton } from 'react-share';
import { useNavigate } from 'react-router-dom';
import { InputAdornment } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@/store';
import { useAuth } from '@/hooks';
import { USER_ROLES } from '@/constants/app';
import {
  fetchEvents,
  createEvent,
  updateEvent,
  deleteEvent,
  enrollEvent,
  markEventInactive,
  clearEventError,
} from '@/store';
import { eventService } from '@/services/events/eventService';
import { ConferenceEvent, CreateEventRequest, UpdateEventRequest, EVENT_CATEGORIES } from '@/constants/types';
import toast from 'react-hot-toast';
import { EventDialog } from './EventDialog';
import { ConfirmDialog } from '@/components/common';

const EVENT_REPORT_REASONS = [
  'Harassment',
  'Fraud or scam',
  'Spam',
  'Misinformation',
  'Hateful speech',
  'Threats or violence',
  'Self-harm',
  'Graphic content',
  'Dangerous or extremist organizations',
  'Sexual content',
  'Fake account',
  'Child exploitation',
  'Illegal goods and services',
  'Infringement',
];

const formatDate = (iso?: string) => {
  if (!iso) return '—';
  const date = new Date(iso);
  const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
  const day = date.toLocaleDateString('en-US', { day: '2-digit' });
  const month = date.toLocaleDateString('en-US', { month: 'long' });
  const time = date.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit', 
    hour12: true 
  });
  return `${weekday} – ${day} ${month} at ${time}`;
};

interface EventCardProps {
  event: ConferenceEvent;
  isSuperAdmin: boolean;
  isEnrolling?: boolean;
  onView: (event: ConferenceEvent) => void;
  onDelete: (event: ConferenceEvent) => void;
  onToggleActive: (event: ConferenceEvent) => void;
  onEnroll: (event: ConferenceEvent) => void;
  onReport: (event: ConferenceEvent) => void;
}

const EventCard: React.FC<EventCardProps> = ({ event, isSuperAdmin, isEnrolling, onView, onDelete, onToggleActive, onEnroll, onReport }) => {
  const theme = useTheme();
  const [shareOpen, setShareOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  const handleMenuOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
  };
  const handleMenuClose = () => setMenuAnchorEl(null);
  const handleReportFromMenu = () => {
    handleMenuClose();
    onReport(event);
  };

  const shareUrl = `https://hrtalky.in/events/${event._id}`;
  const shareTitle = event.eventName;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setSnackbarOpen(true);
      setShareOpen(false);
    });
  };

  const socialIcons = [
    { icon: <CopyLinkIcon />, label: 'Copy link', onClick: handleCopyLink },
    { component: FacebookShareButton, icon: <FacebookIcon />, label: 'Facebook', props: { hashtag: '#HRTalky' } },
    { component: TwitterShareButton, icon: <TwitterIcon />, label: 'X', props: { title: shareTitle, hashtags: ['HRTalky', 'Events'] } },
    { component: LinkedinShareButton, icon: <LinkedInIcon />, label: 'LinkedIn', props: { title: shareTitle, source: 'HRTalky' } },
    { component: WhatsappShareButton, icon: <WhatsAppIcon />, label: 'WhatsApp', props: { title: `${shareTitle}\n\n${shareUrl}`, separator: '' } },
  ];

  return (
    <>
    <Card
      elevation={0}
      onClick={() => onView(event)}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.2s',
        cursor: 'pointer',
        '&:hover': { boxShadow: theme.shadows[4] },
      }}
    >
      {/* Banner */}
      <Box sx={{ position: 'relative' }}>
        {event.eventBanner?.url ? (
          <CardMedia
            component="img"
            height={160}
            image={event.eventBanner.url}
            alt={event.eventName}
            sx={{ objectFit: 'cover' }}
          />
        ) : (
          <Box
            sx={{
              height: 160,
              bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CalendarToday sx={{ fontSize: 48, color: 'text.disabled' }} />
          </Box>
        )}

        {/* Kebab Menu - top right overlay */}
        {!isSuperAdmin && (
          <>
            <Tooltip title="More">
              <IconButton
                size="small"
                onClick={handleMenuOpen}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'rgba(255,255,255,0.9)',
                  color: 'text.primary',
                  boxShadow: 1,
                  '&:hover': { bgcolor: 'white' },
                }}
              >
                <MoreVert fontSize="small" />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={menuAnchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              onClick={(e) => e.stopPropagation()}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={handleReportFromMenu}>
                <ListItemIcon>
                  <Flag fontSize="small" />
                </ListItemIcon>
                <ListItemText>Report</ListItemText>
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>

      <CardContent sx={{ flex: 1, pb: 1 }}>
        {/* Badges row */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          <Chip
            label={event.category}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontSize: '0.7rem' }}
          />
          <Chip
            label={event.eventMode === 'online' ? 'Online' : 'In Person'}
            color={event.eventMode === 'online' ? 'info' : 'default'}
            size="small"
            sx={{ fontSize: '0.7rem' }}
          />
          <Chip
            label={event.ticketType === 'free' ? 'Free' : `₹${event.price}`}
            color={event.ticketType === 'free' ? 'success' : 'warning'}
            size="small"
            sx={{ fontSize: '0.7rem' }}
          />
          {!event.isActive && (
            <Chip label="Inactive" size="small" color="default" sx={{ fontSize: '0.7rem' }} />
          )}
        </Box>

        {/* Title */}
        <Typography
          variant="subtitle1"
          fontWeight={700}
          sx={{
            mb: 1,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {event.eventName}
        </Typography>

        {/* Date */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}>
          <CalendarToday sx={{ fontSize: 14, color: 'text.secondary', mt: '2px' }} />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600, minWidth: 32 }}>Start</Typography>
              <Typography variant="caption" color="text.secondary">{formatDate(event.startDateTime)}</Typography>
            </Box>
            {/* {event.endDateTime && (
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600, minWidth: 32 }}>End</Typography>
                <Typography variant="caption" color="text.secondary">{formatDate(event.endDateTime)}</Typography>
              </Box>
            )} */}
          </Box>
        </Box>

        {/* Venue / Link */}
        {event.eventMode === 'in_person' && event.venueName && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <LocationOn sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary" noWrap>
              {event.venueName}
            </Typography>
          </Box>
        )}
        {event.eventMode === 'online' && event.eventLink && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <LinkIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="primary" noWrap>
              {event.eventLink}
            </Typography>
          </Box>
        )}

        {/* Meta row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
          {event.totalSeats && (
            <Tooltip title="Total Seats">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                <EventSeat sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">{event.totalSeats}</Typography>
              </Box>
            </Tooltip>
          )}
          <Tooltip title={`Visibility: ${event.eventVisibility}`}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              {event.eventVisibility === 'public'
                ? <Public sx={{ fontSize: 14, color: 'text.secondary' }} />
                : <Lock sx={{ fontSize: 14, color: 'text.secondary' }} />}
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                {event.eventVisibility}
              </Typography>
            </Box>
          </Tooltip>
          {event.views !== undefined && (
            <Tooltip title="View Count">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                <Visibility sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {event.views}
                </Typography>
              </Box>
            </Tooltip>
          )}
          {event.certificateProvided && (
            <Tooltip title="Certificate Provided">
              <WorkspacePremium sx={{ fontSize: 14, color: 'success.main' }} />
            </Tooltip>
          )}
        </Box>
      </CardContent>

      <Divider />

      <CardActions sx={{ px: 2, py: 1.5, justifyContent: 'space-between' }} onClick={(e) => e.stopPropagation()}>
        {isSuperAdmin ? (
          <>
            <Tooltip title={event.isActive ? 'Mark Inactive' : 'Active (read-only)'}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => event.isActive && onToggleActive(event)}
                  disabled={!event.isActive}
                  sx={{ color: event.isActive ? 'success.main' : 'text.disabled' }}
                >
                  {event.isActive ? <ToggleOn fontSize="large" /> : <ToggleOff fontSize="large" />}
                </IconButton>
              </span>
            </Tooltip>
            <Box sx={{ display: 'flex' }}>
              <Tooltip title="Share">
                <IconButton size="small" onClick={() => setShareOpen(true)} sx={{ color: 'text.secondary' }}>
                  <ShareOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" onClick={() => onDelete(event)} sx={{ color: 'error.main' }}>
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, width: '100%', cursor: isEnrolling || event.isEnrolled || !event.isActive ? 'not-allowed' : 'pointer'  }}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<HowToReg />}
              onClick={() => onEnroll(event)}
              disabled={isEnrolling || event.isEnrolled || !event.isActive}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              {isEnrolling ? 'Enrolling...' : event.isEnrolled ? 'Enrolled' : 'Interested to Join'}
            </Button>
            <Tooltip title="Share">
              <IconButton size="small" onClick={() => setShareOpen(true)} sx={{ border: '1px solid', borderColor: 'divider' }}>
                <ShareOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </CardActions>
    </Card>

    {/* Share Modal */}
    <Modal open={shareOpen} onClose={() => setShareOpen(false)}>
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 340, bgcolor: 'background.paper',
        boxShadow: 24, p: 3, borderRadius: 3,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>Share Event</Typography>
          <IconButton size="small" onClick={() => setShareOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </Box>
        <Box>
          {socialIcons.map((social, i) => {
            const rowSx = {
              display: 'flex', alignItems: 'center', gap: 1.5,
              px: 2, py: 1, borderRadius: 2, mb: 0.5,
              cursor: 'pointer', width: '100%',
              '&:hover': { bgcolor: 'action.hover' },
            };
            if (social.onClick) {
              return (
                <Box key={i} onClick={social.onClick} sx={rowSx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, flexShrink: 0 }}>
                    {social.icon}
                  </Box>
                  <Typography variant="body2">{social.label}</Typography>
                </Box>
              );
            }
            const ShareBtn = social.component!;
            return (
              <Box key={i} sx={{ ...rowSx, p: 0 }}>
                <ShareBtn url={shareUrl} {...(social.props || {})} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px', width: '100%', color: 'inherit', textDecoration: 'none' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, flexShrink: 0 }}>
                    {social.icon}
                  </Box>
                  <Typography variant="body2">{social.label}</Typography>
                </ShareBtn>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Modal>

    {/* Copy snackbar */}
    <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
      <SnackbarContent
        sx={{ bgcolor: 'success.main', color: 'white', borderRadius: 2 }}
        message={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CheckIcon /><Typography variant="body2" sx={{ color: 'white' }}>Link copied!</Typography></Box>}
        action={<IconButton onClick={() => setSnackbarOpen(false)} color="inherit" size="small"><CloseIcon fontSize="small" /></IconButton>}
      />
    </Snackbar>
    </>
  );
};

const CardSkeleton: React.FC = () => (
  <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
    <Skeleton variant="rectangular" height={160} />
    <CardContent>
      <Skeleton width="60%" height={24} sx={{ mb: 1 }} />
      <Skeleton width="40%" height={20} sx={{ mb: 0.5 }} />
      <Skeleton width="80%" height={20} sx={{ mb: 0.5 }} />
      <Skeleton width="50%" height={20} />
    </CardContent>
  </Card>
);

export const EventsManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === USER_ROLES.SUPER_ADMIN;
  const { events, pagination, isLoading, isCreating, isUpdating, enrollingEventId, error } = useAppSelector(
    (state) => state.events
  );

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [modeFilter, setModeFilter] = useState<string>('all');
  const [upcomingFilter] = useState<boolean>(true);
  const [locationSearch, setLocationSearch] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const [page, setPage] = useState(1);
  const pageSize = 9;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<ConferenceEvent | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<ConferenceEvent | null>(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [eventToReport, setEventToReport] = useState<ConferenceEvent | null>(null);
  const [selectedReportReason, setSelectedReportReason] = useState<string | null>(null);
  const [isReporting, setIsReporting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadEvents();
  }, [categoryFilter, modeFilter, upcomingFilter, page, locationSearch, startDate, endDate, searchQuery]);

  const loadEvents = () => {
    const params: any = { page, limit: pageSize };
    if (searchQuery) params.search = searchQuery;
    if (categoryFilter !== 'all') params.category = categoryFilter;
    if (modeFilter !== 'all') params.eventMode = modeFilter;
    if (upcomingFilter) params.upcoming = true;
    if (locationSearch) params.location = locationSearch;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    dispatch(fetchEvents(params));
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [categoryFilter, modeFilter, upcomingFilter, locationSearch, startDate, endDate, searchQuery]);

  // const handleCreateClick = () => {
  //   setSelectedEvent(null);
  //   setDialogOpen(true);
  // };

  const handleDeleteClick = (event: ConferenceEvent) => {
    setEventToDelete(event);
    setDeleteDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedEvent(null);
  };

  const handleDialogSubmit = async (data: CreateEventRequest | UpdateEventRequest) => {
    try {
      if (selectedEvent) {
        await dispatch(updateEvent({ id: selectedEvent._id, data: data as UpdateEventRequest })).unwrap();
      } else {
        await dispatch(createEvent(data as CreateEventRequest)).unwrap();
      }
      handleDialogClose();
      loadEvents();
    } catch (err) {
      console.error('Failed to save event:', err);
    }
  };

  const handleViewClick = (event: ConferenceEvent) => {
    if (user && !isSuperAdmin) {
      eventService.viewEvent(event._id).catch(console.error);
    }
    navigate(`/events/${event._id}`);
  };

  const handleToggleActiveClick = async (event: ConferenceEvent) => {
    try {
      await dispatch(markEventInactive(event._id)).unwrap();
      toast.success(`"${event.eventName}" marked as inactive`);
    } catch (err: any) {
      toast.error(err || 'Failed to update event status');
    }
  };

  const handleEnrollClick = async (event: ConferenceEvent) => {
    try {
      await dispatch(enrollEvent(event._id)).unwrap();
      toast.success(`Successfully enrolled in "${event.eventName}"!`);
      loadEvents(); // Refresh data to show enrolled status
    } catch (err: any) {
      toast.error(err || 'Failed to enroll in event');
    }
  };

  const handleReportClick = (event: ConferenceEvent) => {
    setEventToReport(event);
    setSelectedReportReason(null);
    setReportDialogOpen(true);
  };

  const handleReportClose = () => {
    setReportDialogOpen(false);
    setEventToReport(null);
    setSelectedReportReason(null);
  };

  const handleReportSubmit = async () => {
    if (!eventToReport || !selectedReportReason) {
      toast.error('Please select a reason for reporting');
      return;
    }
    try {
      setIsReporting(true);
      await eventService.reportEvent(eventToReport._id, selectedReportReason);
      toast.success('Event reported successfully');
      handleReportClose();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to report event');
    } finally {
      setIsReporting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (eventToDelete) {
      try {
        await dispatch(deleteEvent(eventToDelete._id)).unwrap();
        setDeleteDialogOpen(false);
        setEventToDelete(null);
        loadEvents();
      } catch (err) {
        console.error('Failed to delete event:', err);
      }
    }
  };

  const totalPages = pagination ? Math.ceil(pagination.totalItems / pageSize) : 1;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 3 }}>
        Events
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => dispatch(clearEventError())}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Search */}
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search events..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>

        {/* Category Filter */}
        <Grid size={{ xs: 6, sm: 4, md: 3, lg: 4 }}>
          <TextField
            fullWidth
            select
            size="small"
            label="Category"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <MenuItem value="all">All Categories</MenuItem>
            {EVENT_CATEGORIES.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Event Mode Filter */}
        <Grid size={{ xs: 6, sm: 4, md: 3, lg: 3 }}>
          <TextField
            fullWidth
            select
            size="small"
            label="Event Mode"
            value={modeFilter}
            onChange={(e) => {
              setModeFilter(e.target.value);
              if (e.target.value !== 'in_person') setLocationSearch('');
            }}
          >
            <MenuItem value="all">All Modes</MenuItem>
            <MenuItem value="online">Online</MenuItem>
            <MenuItem value="in_person">In Person</MenuItem>
          </TextField>
        </Grid>

        {/* Upcoming Filter */}
        {/* <Grid size={{ xs: 6, sm: 4, md: 3, lg: 2 }}>
          <TextField
            fullWidth
            select
            size="small"
            label="Status"
            value={upcomingFilter ? 'upcoming' : 'all'}
            onChange={(e) => setUpcomingFilter(e.target.value === 'upcoming')}
          >
            <MenuItem value="upcoming">Upcoming</MenuItem>
            <MenuItem value="all">All Events</MenuItem>
          </TextField>
        </Grid> */}

        {/* Location Filter - Only show for In Person events */}
        {modeFilter === 'in_person' && (
          <Grid size={{ xs: 6, sm: 4, md: 3, lg: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Location"
              placeholder="City or State"
              value={locationSearch}
              onChange={(e) => setLocationSearch(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOn fontSize="small" color="disabled" />
                    </InputAdornment>
                  ),
                  endAdornment: locationSearch && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setLocationSearch('')} sx={{ p: 0.2 }}>
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>
        )}

        {/* Start Date Filter */}
        <Grid size={{ xs: 6, sm: 4, md: 3, lg: 3 }}>
          <TextField
            fullWidth
            size="small"
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Grid>

        {/* End Date Filter */}
        <Grid size={{ xs: 6, sm: 4, md: 3, lg: 3 }}>
          <TextField
            fullWidth
            size="small"
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Grid>
      </Grid>

      {/* Card Grid */}
      {isLoading ? (
        <Grid container spacing={3}>
          {Array.from({ length: pageSize }).map((_, i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 3 }}>
              <CardSkeleton />
            </Grid>
          ))}
        </Grid>
      ) : (events ?? []).filter(Boolean).length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 10,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 3,
          }}
        >
          <CalendarToday sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No events found
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
            Try adjusting your filters or create a new event.
          </Typography>
        
            {/* <Button variant="contained" startIcon={<Add />} onClick={handleCreateClick}>
              Create Event
            </Button> */}
          
        </Box>
      ) : (
        <Grid container spacing={3}>
          {(events ?? []).filter(Boolean).map((event) => (
            <Grid key={event._id} size={{ xs: 12, sm: 6, md: 6 }}>
              <EventCard
                event={event}
                isSuperAdmin={isSuperAdmin}
                isEnrolling={enrollingEventId === event._id}
                onView={handleViewClick}
                onDelete={handleDeleteClick}
                onToggleActive={handleToggleActiveClick}
                onEnroll={handleEnrollClick}
                onReport={handleReportClick}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}

      <EventDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleDialogSubmit}
        event={selectedEvent}
        isLoading={isCreating || isUpdating}
      />


      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Event"
        message={`Are you sure you want to delete "${eventToDelete?.eventName}"? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setEventToDelete(null);
        }}
        severity="error"
      />

      {/* Report Event Dialog */}
      <Dialog open={reportDialogOpen} onClose={handleReportClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          Report "{eventToReport?.eventName}"
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select your reporting reason
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {EVENT_REPORT_REASONS.map((reason) => (
              <Chip
                key={reason}
                label={reason}
                onClick={() => setSelectedReportReason(reason)}
                color={selectedReportReason === reason ? 'primary' : 'default'}
                variant={selectedReportReason === reason ? 'filled' : 'outlined'}
                sx={{
                  borderRadius: '16px',
                  fontWeight: 500,
                  '&:hover': {
                    bgcolor: selectedReportReason === reason ? 'primary.dark' : 'action.hover',
                  },
                }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleReportClose} color="inherit" disabled={isReporting}>
            Cancel
          </Button>
          <Button
            onClick={handleReportSubmit}
            color="primary"
            variant="contained"
            disabled={!selectedReportReason || isReporting}
            sx={{ borderRadius: '16px', px: 3 }}
          >
            {isReporting ? 'Reporting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
